import {Badge, MantineColor} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {getRelativeURL} from 'BenchlingUtil';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import {AsyncTaskStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {
  RecentAsyncTasksQuery,
  RecentAsyncTasksQueryVariables,
  RecentAsyncTasksQuery_recentAsyncTasks_AsyncTaskNode as RecentAsyncTaskNode,
} from 'nav/sidepanel/home/__generated/RecentAsyncTasksQuery';
import {RECENT_ASYNC_TASKS_QUERY} from 'nav/sidepanel/home/RecentActivitySection';
import {DEFAULT_POLL_INTERVAL_MS} from 'nav/sidepanel/home/RecentActivitySection';
import useRecentActivityPolling from 'nav/sidepanel/home/useRecentActivityPolling';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import APIUtil from 'util/APIUtil';

import CardSection, {
  CardSectionEmptyState,
  CardSectionMessageRow,
  CardSectionRowList,
  CardSectionRowProps,
  renderQueryLoadingOrError,
} from './CardSection';
import {
  DEFAULT_BADGE_PROPS,
  HOMEPAGE_SECTION_TEST_COMPONENT,
  INITIAL_ROWS_TO_DISPLAY,
  MAX_ROWS_TO_DISPLAY,
} from './HomepageConstants';
import {HOMEPAGE_SECTION_EMPTY_STATES} from './HomepageEmptyStates';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.ACTIVITY;

export const RECENT_ACTIVITY_LOOKBACK_DAYS = 7;
export const RECENT_ACTIVITY_LIMIT = MAX_ROWS_TO_DISPLAY;

/** @package */
export function getStatusDisplay(status: AsyncTaskStatus): {
  label: string;
  color: MantineColor;
  sortOrder: number;
} {
  switch (status) {
    case AsyncTaskStatus.IN_PROGRESS:
      return {label: 'In progress', color: 'blue', sortOrder: 0};
    case AsyncTaskStatus.FAILED:
      return {label: 'Failed', color: 'red', sortOrder: 1};
    case AsyncTaskStatus.SUCCEEDED:
      return {label: 'Completed', color: 'green', sortOrder: 2};
    default:
      return {label: 'Unknown', color: 'gray', sortOrder: Number.MAX_SAFE_INTEGER};
  }
}

const EMPTY_TASKS: ReadonlyArray<RecentAsyncTaskNode> = [];

/** Shows recent async tasks (bulk imports, exports, etc.) with status badges and polling while in progress. */
export default function ActivitySection(): JSX.Element {
  const [showMore, setShowMore] = useState(false);
  const queryResult = useBenchlingQuery<RecentAsyncTasksQuery, RecentAsyncTasksQueryVariables>(
    RECENT_ASYNC_TASKS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
      variables: {lookbackDays: RECENT_ACTIVITY_LOOKBACK_DAYS, limit: RECENT_ACTIVITY_LIMIT},
    }
  );

  const recentAsyncTasks =
    queryResult.type === QueryResultType.SUCCESS ? queryResult.data.recentAsyncTasks : EMPTY_TASKS;

  const tasks = useMemo(
    () =>
      [...recentAsyncTasks].sort((a, b) => {
        const statusDiff = getStatusDisplay(a.status).sortOrder - getStatusDisplay(b.status).sortOrder;
        if (statusDiff !== 0) {
          return statusDiff;
        }
        return b.createdAt.localeCompare(a.createdAt);
      }),
    [recentAsyncTasks]
  );

  const hasInProgress = useMemo(
    () => tasks.some((task) => task.status === AsyncTaskStatus.IN_PROGRESS),
    [tasks]
  );

  useRecentActivityPolling({
    hasInProgress,
    pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
    startPolling: queryResult.startPolling,
    stopPolling: queryResult.stopPolling,
    refetch: queryResult.refetch,
  });

  const loadingOrError = renderQueryLoadingOrError(queryResult, 'Error loading activity', TEST_COMPONENT);

  const visibleTasks = tasks.slice(0, showMore ? MAX_ROWS_TO_DISPLAY : INITIAL_ROWS_TO_DISPLAY);

  const activityRows: Array<CardSectionRowProps> = visibleTasks.map((task) => {
    const {label, color} = getStatusDisplay(task.status);
    const isChatSession = task.sourceObjectApiId ? APIUtil.isChatSession(task.sourceObjectApiId) : false;
    const url = task.detailsUrl != null ? (getRelativeURL(task.detailsUrl) ?? task.detailsUrl) : null;

    return {
      id: task.id,
      name: task.displayName ?? task.id,
      href: url ?? undefined,
      onClick: () => {
        trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_RECENT_ACTIVITY_TASK);
        if (!isChatSession) {
          SidepanelActions.close({autoOpenFile: false});
        }
      },
      rightContent: (
        <Badge color={color} {...DEFAULT_BADGE_PROPS}>
          {label}
        </Badge>
      ),
    };
  });

  return (
    <CardSection title="Running now" dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}>
      {loadingOrError}
      {queryResult.type === QueryResultType.SUCCESS && queryResult.data.recentAsyncTasks.length === 0 && (
        <CardSectionEmptyState
          {...HOMEPAGE_SECTION_EMPTY_STATES.runningNow}
          dataTestAttrs={{component: TEST_COMPONENT, element: 'messageRow', key: 'empty'}}
        />
      )}
      {queryResult.type === QueryResultType.SUCCESS && queryResult.data.recentAsyncTasks.length > 0 && (
        <CardSectionRowList rows={activityRows} />
      )}
      {tasks.length > INITIAL_ROWS_TO_DISPLAY && (
        <CardSectionMessageRow
          message={showMore ? 'Show less' : 'Show more'}
          onClick={() => {
            setShowMore(!showMore);
          }}
        />
      )}
    </CardSection>
  );
}
